/**
 * The company's settings on SellGo's own database: its currency symbol, and
 * whether the manager has done the first-time setup (currency, VAT, payment
 * methods). Until then the app shows the setup instead of its screens.
 *
 * Read at sign-in and when the app opens; remembered on the device so the
 * app opens straight away (and offline). On Odoo none of this applies.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, getBackend } from '@/api/apiClient';
import { setCurrencySymbol } from '@/lib/currency';

const LS_SETUP = 'sellgo_setup_done';

/** Keep what the app needs from GET /sellgo_company. */
export function rememberCompany(company) {
  if (!company) return;
  if (company.currency_symbol) setCurrencySymbol(company.currency_symbol);
  try {
    localStorage.setItem(LS_SETUP, company.setup_done === false ? '0' : '1');
  } catch {
    // storage blocked: the app asks the server every time
  }
}

function savedSetup() {
  try {
    return localStorage.getItem(LS_SETUP);
  } catch {
    return null;
  }
}

/**
 * { checking, needed, company, failed, refresh } for the signed-in user (uid).
 * `needed`: the company isn't set up yet. A failed read (offline) only blocks
 * the app if the device already knew the setup wasn't done.
 */
export function useCompanySetup(uid) {
  const onSellGoDb = getBackend() === 'supabase';
  const [tick, setTick] = useState(0);
  const [state, setState] = useState({ checking: false, needed: false, company: null, failed: false });

  useEffect(() => {
    if (!onSellGoDb || !uid) {
      setState({ checking: false, needed: false, company: null, failed: false });
      return undefined;
    }
    let alive = true;
    const saved = savedSetup();
    setState((s) => ({ ...s, checking: saved === null, needed: saved === '0', failed: false }));
    api.get('/sellgo_company')
      .then((res) => {
        if (!alive) return;
        rememberCompany(res?.data);
        setState({ checking: false, needed: res?.data?.setup_done === false, company: res?.data || null, failed: false });
      })
      .catch(() => {
        if (alive) setState((s) => ({ ...s, checking: false, failed: true }));
      });
    return () => { alive = false; };
  }, [onSellGoDb, uid, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, refresh };
}
